//selectors

import { createSelector } from 'reselect';
import { GetRegion } from 'constants.js';
import _ from 'lodash';
import {
  CLARO_TYPES,
  LABEL_SUFFIX,
  PREDICTSEARCH_PREFIX,
} from 'containers/PredictiveSearch/constants';
import { getTranslations } from '../../reducers/translationReducer';
import Utils from '../../utils/Utils';

const APA_KEYS = ['predictSearch_button_see_more', 'buscar'];

export const getUserState = state => _.get(state, 'user');

export const getRegion = createSelector(
  [getUserState],
  user => _.get(user, 'region'),
);

export const getSearchTranslations = createSelector(
  [getTranslations],
  translations => _.pick(translations, APA_KEYS),
);

// this is not selectors to the redux store
// they are pure functions to normalize the response of the predictive search API
// transforms a messy JSON into a friendly object
// in a functional way

const translate = path =>
  window.utils.getMeta.translations(
    `${PREDICTSEARCH_PREFIX}_${path}_${LABEL_SUFFIX}`,
  );

export const generateValueFor = (item, paths) => {
  const getPaths = obj => _.map(paths, path => _.get(obj, path));
  const joinNames = names => _.join(names, ' ');
  return _.flow(
    getPaths,
    _.compact,
    _.concat,
    joinNames,
  )(item);
};

const generateID = item => _.result(item, 'id', _.uniqueId());

const formatProviderResult = (result, type) =>
  _.map(result, item => {
    const id = generateID(item);

    const title = generateValueFor(item, [
      'title',
      'name',
      'metadatas.name',
      'metadatas.lastname',
      'first_name',
      'last_name',
    ]);

    const image = generateValueFor(item, ['image_small', 'channel_image']);

    // rest :: Object -> Object { url, isLive }
    const rest = Utils.createUrlSearch(item, type);
    // meta :: Object -> { format_types, proveedor_name }
    const meta = _.pick(item, ['format_types', 'proveedor_name']);

    return { title, id, image, meta, type, item, ...rest };
  });

const transformProviders = (object, level = 0, name = '', depth = '') =>
  _.flatMap(object, (value, key) => {
    if (level === 0) name = key;

    if (_.isArray(value)) {
      // order :: Array blacklist
      const order = _.includes([...CLARO_TYPES], name)
        ? name
        : `${name}_${key}`;

      const transformedName = _.toUpper(depth + key);

      return {
        name: translate(order),
        results: formatProviderResult(value, transformedName),
        order,
      };
    }

    return transformProviders(value, level + 1, name, `${key}_${depth}`);
  });

const orderCollection = collection => {
  const defaultOrderList = [
    'suggest',
    'movies',
    'series',
    'live_channels',
    'epgs_now',
    'epgs_future',
    'epgs_past',
    'fox_serie',
    'fox_movie',
    'fox_live_channel',
    'foxv3_serie',
    'foxv3_movie',
    'foxv3_live_channel',
    'hbo_movie',
    'hbo_serie',
    'hbo_live_channel',
    'crackle_movie',
    'crackle_serie',
    'noggin_movie',
    'noggin_serie',
    'indycar_serie',
    'indycar_movie',
    'indycar_live_channel',
    'edye_serie',
    'edye_movie',
    'edye_live_channel',
    'paramount_serie',
    'paramount_movie',
    'talents',
    'genres',
    'users',
  ];

  const orderFromAPA = _.flow(
    obj => _.get(obj, ['metadata', 'search_priority']),
    obj => JSON.parse(obj),
    obj =>
      _.get(obj, [GetRegion(), 'searchPriority'], false) ||
      _.get(obj, ['default', 'searchPriority']),
  )(window);

  const orderBy = orderFromAPA || defaultOrderList;

  return _.sortBy(collection, ({ order }) => orderBy.indexOf(order));
};

export const normalize = response => {
  let prediction = _.get(response, 'prediction');

  const suggest = _.get(response, ['suggest', '0', 'common']);

  const talentProvider = _.flow(
    obj => _.get(obj, ['metadata', 'external_metadata']),
    obj => JSON.parse(obj),
    obj => _.get(obj, ['default', 'provider']),
  )(window);

  const externalTalents = _.compact(
    _.flatten([
      _.get(prediction, ['external_talents', 'gracenote']),
      _.get(prediction, ['external_talents', '']),
    ]),
  );

  const amcoTalents = _.get(prediction, ['talents', 'talent']);

  const isGracenoteSource =
    _.isEqual(talentProvider, 'gracenote') && !_.isEmpty(externalTalents);

  const addProvider = (talents, provider) =>
    _.map(talents, talent => ({ ...talent, provider }));

  const talents = isGracenoteSource
    ? addProvider(externalTalents, 3)
    : addProvider(amcoTalents, 2);

  // prediction :: Object -> dissoc taents Object
  prediction = _.omit(prediction, ['talents', 'external_talents']);

  if (!_.isEmpty(talents)) {
    prediction = _.merge(prediction, { talents });
  }

  // P :: Array -> Transfomrmed Array
  const P = transformProviders(prediction);

  if (_.isEmpty(suggest)) return orderCollection(P);
  // S :: Array -> Transfomrmed Array
  const S = transformProviders({ suggest: [suggest] });

  return orderCollection(_.concat(S, P));
};
